import { Icon, Tag } from "@thetreep/ui";
import { useEffect, useRef, useState } from "react";
import { getMenuActions, MenuActionProps } from "@utils/profileMenu";
import { User, Role } from "@gen/user/v1/user_pb";
import { useTranslation } from "react-i18next";

export interface ProfileMenuProps {
  user: User;
  onLogout: () => void;
  onNavigate: (id: string) => void;
}

export function ProfileMenu({ user, onLogout, onNavigate }: ProfileMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const menuActions: MenuActionProps[] = getMenuActions(
    user,
    onLogout,
    onNavigate,
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleAction = (action?: () => void) => {
    setIsOpen(false);
    action?.();
  };

  const roles: Partial<
    Record<Role, { text: string; bg: string; color: string }>
  > = {
    [Role.CAISSE_ASSOCIATE]: {
      text: t("user.role.caisseAssociate"),
      bg: "#CCE7FFCC",
      color: "#4646BB",
    },
    [Role.TRAVEL_AGENT]: {
      text: t("user.role.travelAgent"),
      bg: "#DAFDEBCC",
      color: "#218C26",
    },
    [Role.ADMIN]: {
      text: t("user.role.admin"),
      bg: "#FFE0EACC",
      color: "#B04D56",
    },
  };

  const agencyUser = `${user?.agencyName}`;
  const caissesUser = user.caisseIds
    .map((id, index) => `${id} - ${user.caisseName[index]}`)
    .join(", ");

  return (
    <div data-testid="profile" ref={menuRef} className="relative">
      {/* Profile Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-100 transition-colors"
      >
        <div className="hidden md:flex gap-2 ">
          <div className="text-base truncate max-w-[200px]">
            {user.firstName} {user.lastName}
          </div>

          <div
            className={`transform transition-transform ${
              isOpen ? "rotate-180" : ""
            }`}
          >
            <Icon src={"down_line"} />
          </div>
        </div>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="flex flex-col gap-2 p-6 bg-white z-[100] rounded-lg shadow-[0px_0px_48px_0px_rgba(0,0,0,0.16)]  gap-4 absolute right-0 mt-2 ">
          <div className="flex flex-col ">
            <p className="text-sm ">
              {user.firstName} {user.lastName}
            </p>
            <p className="text-xs">{user.email}</p>
            {(user.role === Role.CAISSE_ASSOCIATE ||
              user.role === Role.TRAVEL_AGENT ||
              user.role === Role.ADMIN) && (
              <Tag
                size="small"
                className="!rounded-md my-2"
                text={roles[user.role]?.text}
                bg={roles[user.role]?.bg}
                color={roles[user.role]?.color}
              />
            )}

            {user.role === Role.TRAVEL_AGENT && (
              <p className="text-xs">{agencyUser}</p>
            )}

            {user.role === Role.CAISSE_ASSOCIATE && (
              <p className="text-xs">{caissesUser}</p>
            )}
          </div>

          <div>
            {menuActions.map(
              (action, index) =>
                action.isVisible && (
                  <button
                    key={index}
                    data-testid={action.testId}
                    onClick={() => handleAction(action.action)}
                    className="flex items-center font-bold  min-w-[276px] gap-2 w-full px-6 py-3 text-sm hover:bg-gray-100 "
                  >
                    <Icon src={action.iconSrc} />
                    {action.label}
                  </button>
                ),
            )}
          </div>
        </div>
      )}
    </div>
  );
}
